import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Lock, CheckCircle2, AlertTriangle, ArrowLeft } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ResetPasswordPage = () => {
  const { setSessionFromAuthResponse } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token');

  const [state, setState] = useState('verifying'); // verifying | valid | invalid | done
  const [email, setEmail] = useState(null);
  const [pwd, setPwd] = useState('');
  const [pwd2, setPwd2] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!token) {
      setState('invalid');
      return;
    }
    (async () => {
      try {
        const { data } = await axios.post(`${API}/auth/password-reset-verify`, { token });
        if (data.valid) {
          setEmail(data.email);
          setState('valid');
        } else {
          setState('invalid');
        }
      } catch {
        setState('invalid');
      }
    })();
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    if (pwd.length < 4) {
      setError('Password must be at least 4 characters.');
      return;
    }
    if (pwd !== pwd2) {
      setError("Passwords don't match.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/password-reset-complete`, {
        token,
        new_password: pwd,
      });
      // Auto-sign in with the fresh token returned
      setSessionFromAuthResponse(data);
      setState('done');
      toast({
        title: 'Password updated',
        description: 'You are now signed in with your new password.',
      });
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || 'Could not update password.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="pt-28 pb-20 bg-[#0a0608] min-h-screen">
      <div className="max-w-md mx-auto px-6">
        <Link
          to="/account"
          className="inline-flex items-center gap-2 text-[#f5b400] font-nav text-xs hover:text-white transition-colors"
        >
          <ArrowLeft size={14} /> BACK TO ACCOUNT
        </Link>

        <div className="mt-6 rounded-2xl border border-white/10 bg-black/60 p-8 backdrop-blur fade-up">
          {state === 'verifying' && (
            <div className="text-center py-6">
              <div className="w-10 h-10 mx-auto border-2 border-[#f5b400] border-t-transparent rounded-full animate-spin" />
              <p className="font-body text-white/75 text-sm mt-4">Verifying your link…</p>
            </div>
          )}

          {state === 'invalid' && (
            <div className="text-center py-6">
              <AlertTriangle size={48} className="text-red-400 mx-auto" />
              <h2 className="font-heading text-white text-2xl mt-3">Link expired or invalid</h2>
              <p className="font-body text-white/70 text-sm mt-2">
                This reset link is no longer valid. Please request a new one from the sign-in screen.
              </p>
              <Link
                to="/account"
                className="mt-6 inline-flex bg-[#f5b400] hover:bg-[#ffc630] text-black font-nav text-xs px-6 py-3 rounded-md transition-colors"
              >
                BACK TO SIGN IN
              </Link>
            </div>
          )}

          {state === 'valid' && (
            <>
              <p className="font-nav text-[#f5b400] text-[10px] tracking-widest text-center">
                PASSWORD RESET
              </p>
              <h2 className="font-heading text-white text-2xl text-center mt-1">
                Choose a new password
              </h2>
              <p className="font-body text-white/70 text-xs text-center mt-2">
                For <b className="text-white">{email}</b>
              </p>

              <form onSubmit={submit} className="mt-6 space-y-4">
                <div>
                  <label className="font-nav text-[10px] tracking-widest text-white/60">
                    NEW PASSWORD
                  </label>
                  <div className="mt-1 flex items-center gap-2 bg-black/50 border border-white/15 focus-within:border-[#f5b400] rounded-md px-3 py-2 transition-colors">
                    <Lock size={16} className="text-[#f5b400]" />
                    <input
                      type="password"
                      value={pwd}
                      onChange={(e) => setPwd(e.target.value)}
                      placeholder="••••••••"
                      minLength={4}
                      required
                      className="flex-1 bg-transparent outline-none text-white text-sm placeholder:text-white/30"
                      autoFocus
                    />
                  </div>
                </div>
                <div>
                  <label className="font-nav text-[10px] tracking-widest text-white/60">
                    CONFIRM PASSWORD
                  </label>
                  <div className="mt-1 flex items-center gap-2 bg-black/50 border border-white/15 focus-within:border-[#f5b400] rounded-md px-3 py-2 transition-colors">
                    <Lock size={16} className="text-[#f5b400]" />
                    <input
                      type="password"
                      value={pwd2}
                      onChange={(e) => setPwd2(e.target.value)}
                      placeholder="••••••••"
                      minLength={4}
                      required
                      className="flex-1 bg-transparent outline-none text-white text-sm placeholder:text-white/30"
                    />
                  </div>
                </div>

                {error && (
                  <p className="text-red-400 text-xs font-body bg-red-500/10 border border-red-500/30 rounded-md p-2">
                    {error}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#f5b400] hover:bg-[#ffc630] disabled:opacity-60 text-black font-nav text-xs py-3 rounded-md transition-colors"
                >
                  {loading ? 'UPDATING…' : 'SAVE NEW PASSWORD'}
                </button>
              </form>
            </>
          )}

          {state === 'done' && (
            <div className="text-center py-6">
              <CheckCircle2 size={56} className="text-[#f5b400] mx-auto" />
              <h2 className="font-heading text-white text-2xl mt-3">Password updated</h2>
              <p className="font-body text-white/70 text-sm mt-2">
                You're now signed in with your new password.
              </p>
              <button
                onClick={() => navigate('/account')}
                className="mt-6 bg-[#f5b400] hover:bg-[#ffc630] text-black font-nav text-xs px-6 py-3 rounded-md transition-colors"
              >
                GO TO MY ACCOUNT
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
